class ArticleAnchorNavigation {
    constructor(windowObject = window, documentObject = document) {
        this.window = windowObject;
        this.document = documentObject;
        this.root = null;
        this.articleRoot = null;
        this.links = [];
        this.targets = [];
        this.markers = [];
        this.desktop = null;
        this.toggle = null;
        this.currentLabel = null;
        this.counter = null;
        this.headerHeight = 0;
        this.updateScheduled = false;
        this.activeId = null;
        this.pinned = false;
        this.hovered = false;
        this.focused = false;
        this.hoverCloseTimer = null;
        this.resizeObserver = null;
    }

    init(root) {
        if (!root || root.dataset.articleAnchorInitialized === 'true') {
            return;
        }

        const links = Array.from(root.querySelectorAll('[data-article-anchor-link]'));
        const targetsById = new Map();

        links.forEach((link) => {
            const id = this.anchorId(link);
            const target = id ? this.document.getElementById(id) : null;
            if (target && !targetsById.has(id)) {
                targetsById.set(id, {id, target});
            }
        });

        const targets = Array.from(targetsById.values());
        if (targets.length < 2) {
            root.hidden = true;
            return;
        }

        root.dataset.articleAnchorInitialized = 'true';
        this.root = root;
        this.articleRoot = root.closest('[data-article-root]') || this.document.documentElement;
        this.links = links;
        this.targets = targets;
        this.desktop = root.querySelector('[data-article-anchor-desktop]');
        this.toggle = root.querySelector('[data-article-anchor-toggle]');
        this.currentLabel = root.querySelector('[data-article-anchor-current]');
        this.counter = root.querySelector('[data-article-anchor-counter]');
        this.markers = Array.from(root.querySelectorAll('[data-article-anchor-marker]'))
            .map((marker) => ({
                marker,
                id: marker.dataset.anchorId || '',
            }))
            .filter((item) => item.id !== '');

        root.addEventListener('click', (event) => this.handleClick(event));
        this.toggle?.addEventListener('click', () => this.togglePinned());
        this.desktop?.addEventListener('mouseenter', () => {
            this.clearHoverClose();
            this.hovered = true;
            this.updateExpandedState();
        });
        this.desktop?.addEventListener('mouseleave', () => {
            this.scheduleHoverClose();
        });
        this.desktop?.addEventListener('focusin', () => {
            this.clearHoverClose();
            this.focused = true;
            this.updateExpandedState();
        });
        this.desktop?.addEventListener('focusout', (event) => {
            if (!event.relatedTarget || !this.desktop.contains(event.relatedTarget)) {
                this.focused = false;
                this.updateExpandedState();
            }
        });
        this.window.addEventListener('scroll', () => this.scheduleUpdate(), {passive: true});
        this.window.addEventListener('resize', () => this.handleResize(), {passive: true});
        this.window.addEventListener('hashchange', () => this.handleHashChange());
        this.window.addEventListener('load', () => this.updateGeometry(), {once: true});

        if (this.window.ResizeObserver) {
            this.resizeObserver = new this.window.ResizeObserver(() => {
                this.updateGeometry();
                this.scheduleUpdate();
            });
            this.resizeObserver.observe(this.articleRoot);
        }

        this.updateOffsets();
        this.updateGeometry();
        this.handleInitialHash();
        this.updateActive();
        this.updateProgress();
    }

    anchorId(link) {
        const href = link.getAttribute('href') || '';
        if (!href.startsWith('#') || href.length < 2) {
            return null;
        }

        try {
            return decodeURIComponent(href.slice(1));
        } catch (error) {
            return href.slice(1);
        }
    }

    handleClick(event) {
        const link = event.target.closest('[data-article-anchor-link]');
        if (!link || !this.root.contains(link)) {
            return;
        }

        const id = this.anchorId(link);
        const item = this.targets.find((candidate) => candidate.id === id);
        if (!item) {
            return;
        }

        this.setActive(item.id);
        this.setPinned(false);

        // Keep native fragment navigation/history, then move keyboard/screen-reader
        // focus to the destination without causing a second scroll jump.
        this.window.setTimeout(() => {
            item.target.focus({preventScroll: true});
        }, 0);
    }

    clearHoverClose() {
        if (this.hoverCloseTimer === null) {
            return;
        }

        this.window.clearTimeout?.(this.hoverCloseTimer);
        this.hoverCloseTimer = null;
    }

    scheduleHoverClose() {
        this.clearHoverClose();
        this.hoverCloseTimer = this.window.setTimeout(() => {
            this.hoverCloseTimer = null;
            this.hovered = false;
            this.updateExpandedState();
        }, 140);
    }

    togglePinned() {
        this.setPinned(!this.pinned);
    }

    setPinned(pinned) {
        this.pinned = pinned;
        this.desktop?.classList.toggle('is-pinned', pinned);
        this.updateExpandedState();
    }

    updateExpandedState() {
        this.toggle?.setAttribute(
            'aria-expanded',
            String(this.pinned || this.hovered || this.focused),
        );
    }

    handleResize() {
        this.updateOffsets();
        this.updateGeometry();
        this.scheduleUpdate();
    }

    handleHashChange() {
        const id = this.currentHashId();
        if (id && this.targets.some((item) => item.id === id)) {
            this.setActive(id);
        }
        this.scheduleUpdate();
    }

    handleInitialHash() {
        const id = this.currentHashId();
        const item = this.targets.find((candidate) => candidate.id === id);
        if (!item) {
            return;
        }

        this.setActive(item.id);

        // The browser can resolve the hash before the sticky-header offset is known.
        this.window.requestAnimationFrame(() => {
            item.target.scrollIntoView({block: 'start', behavior: 'auto'});
        });
    }

    currentHashId() {
        const hash = this.window.location?.hash || '';
        if (!hash.startsWith('#') || hash.length < 2) {
            return null;
        }

        try {
            return decodeURIComponent(hash.slice(1));
        } catch (error) {
            return hash.slice(1);
        }
    }

    updateOffsets() {
        const stickyHeaders = this.document.querySelectorAll
            ? Array.from(this.document.querySelectorAll('body > .sticky-top'))
            : Array.from(this.document.body?.children || [])
                .filter((element) => element.classList?.contains('sticky-top'));

        this.headerHeight = stickyHeaders.reduce(
            (height, element) => Math.max(height, Math.ceil(element.getBoundingClientRect().height)),
            0,
        );

        this.articleRoot.style.setProperty('--article-anchor-offset', `${this.headerHeight}px`);
    }

    updateGeometry() {
        if (!this.articleRoot || this.targets.length === 0) {
            return;
        }

        this.updateRailPlacement();

        const articleRect = this.articleRoot.getBoundingClientRect();
        const articleTop = articleRect.top + this.scrollY();
        const articleHeight = Math.max(articleRect.height || 0, this.articleRoot.scrollHeight || 0, 1);

        this.markers.forEach((item) => {
            const target = this.targets.find((candidate) => candidate.id === item.id)?.target;
            if (!target) {
                return;
            }

            const targetTop = target.getBoundingClientRect().top + this.scrollY();
            const ratio = this.clamp((targetTop - articleTop) / articleHeight, 0, 1);
            item.marker.style.setProperty('--article-anchor-position', `${ratio * 100}%`);
        });
    }

    updateRailPlacement() {
        if (!this.root) {
            return;
        }

        const viewportWidth = Number(
            this.document.documentElement?.clientWidth
            || this.window.innerWidth
            || 0,
        );
        if (viewportWidth <= 0) {
            return;
        }

        // Prefer the gutter next to a normal Bootstrap container. Wide/full-screen
        // Article sections deliberately do not qualify: when no constrained content
        // exists, the rail falls back to a small viewport-edge overlay.
        const constrainedTarget = this.targets.find(({target}) =>
            target.classList?.contains('container')
            && !target.classList?.contains('container-fluid'),
        );

        const viewportMargin = 12;
        const contentGap = 12;
        const railWidth = 36; // 2.25rem, kept in sync with the desktop rail CSS.
        let railRight = 16;
        let placement = 'overlay';

        if (constrainedTarget) {
            const contentRect = constrainedTarget.target.getBoundingClientRect();
            const availableRightGutter = Math.max(0, viewportWidth - contentRect.right);
            const requiredGutter = railWidth + contentGap + viewportMargin;

            if (availableRightGutter >= requiredGutter) {
                railRight = availableRightGutter - contentGap - railWidth;
                placement = 'outside';
            }
        }

        this.root.style.setProperty(
            '--article-anchor-rail-right',
            `${Math.round(Math.max(viewportMargin, railRight))}px`,
        );
        this.root.dataset.articleAnchorPlacement = placement;
    }

    scheduleUpdate() {
        if (this.updateScheduled) {
            return;
        }

        this.updateScheduled = true;
        this.window.requestAnimationFrame(() => {
            this.updateScheduled = false;
            this.updateActive();
            this.updateProgress();
        });
    }

    updateActive() {
        if (!this.root || this.targets.length === 0) {
            return;
        }

        const activationLine = this.headerHeight + 24;
        let activeId = null;

        for (const item of this.targets) {
            if (item.target.getBoundingClientRect().top <= activationLine) {
                activeId = item.id;
                continue;
            }
            break;
        }

        this.setActive(activeId);
    }

    updateProgress() {
        if (!this.articleRoot) {
            return;
        }

        const articleRect = this.articleRoot.getBoundingClientRect();
        const articleTop = articleRect.top + this.scrollY();
        const articleHeight = Math.max(articleRect.height || 0, this.articleRoot.scrollHeight || 0, 1);
        const readingLine = this.scrollY() + this.headerHeight + 24;
        const ratio = this.clamp((readingLine - articleTop) / articleHeight, 0, 1);

        this.root.style.setProperty('--article-anchor-progress', `${ratio * 100}%`);
    }

    setActive(id) {
        if (this.activeId === id) {
            return;
        }

        this.activeId = id;
        let currentText = '';
        let currentIndex = 0;

        this.links.forEach((link) => {
            const isCurrent = id !== null && this.anchorId(link) === id;
            if (isCurrent) {
                link.setAttribute('aria-current', 'location');
                currentText ||= link.textContent.trim();
            } else {
                link.removeAttribute('aria-current');
            }
        });

        this.markers.forEach((item) => {
            item.marker.classList.toggle('is-current', id !== null && item.id === id);
        });

        if (id !== null) {
            const index = this.targets.findIndex((item) => item.id === id);
            currentIndex = index >= 0 ? index + 1 : 0;
        }

        if (this.currentLabel) {
            this.currentLabel.textContent = currentText;
        }
        if (this.counter) {
            this.counter.textContent = `${currentIndex} / ${this.targets.length}`;
        }
    }

    scrollY() {
        return Number(this.window.scrollY ?? this.window.pageYOffset ?? 0);
    }

    clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
    }
}

export default ArticleAnchorNavigation;
